/**
 * Os perfis da campanha CPTu lado a lado ou sobrepostos (onda 316; a prancheta «Perfis lado a lado»): uma
 * grandeza, por profundidade ou por cota, em faixas de `passo` m.
 *
 *  · Sobrepostos: cada vertical num traço fino, a destacada (clique na legenda ou no traço) em laranja; a média
 *    do `perfilMedio` do motor no traço grosso, com a faixa ± desvio e o n escrito («10 verticais»). A faixa
 *    com n < 2 fica sem desvio.
 *  · Lado a lado: uma coluna estreita por vertical, com o mesmo eixo, na ordem dada (as estações da linha
 *    escolhida, ou o código). Por cota, as sem cota vêm listadas («sem cota: CPTu11»).
 *
 * A leitura de uma faixa (o ponteiro ou ↑ ↓) sai num `aria-live`: «cota −9,375 m · média 2,58 MPa ± 6,36 · 10
 * verticais». A ponte de teste `caixa.ponteDosPerfis = { faixa(centro), tracos(), colunas() }`. As cores são
 * tokens; a cor nunca sozinha — o código vai escrito na legenda e no topo de cada coluna. O mesmo componente
 * serve ao Portal (`mostrarNaoHomologadas` por propriedade).
 *
 * Onda 347 (R13, o zoom de profundidade; `zoomDeProfundidade.tsx`): com `aoZoom`, arrastar para baixo sobre o desenho dá o
 * zoom no trecho — um eixo só, o de todas as verticais (lado a lado ou sobrepostas) —, e arrastar para cima volta ao eixo
 * inteiro; no celular, com o toque longo. No automático, a escala horizontal sai das leituras do trecho.
 */
import type { JSX } from 'react';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { GRANDEZAS, perfilMedio } from '../motor';
import type { Grandeza } from '../motor';
import { ControleDaEscala } from './ControleDaEscala';
import { dizimarPorPixel } from './dizimar';
import { dominioDaEscala, limitesFixos, marcasDaEscala } from './escalaDoGrafico';
import type { EscalaDoGrafico } from './escalaDoGrafico';
import { dominioDe, escala, marcas, numeroBr } from './escalas';
import type { VerticalDaAnalise } from './espacialDaCampanha';
import { marcaDaColuna } from './homologacao';
import { useLargura } from './useLargura';
import { ARRASTO_DO_ZOOM, BarraDoZoom, useArrastoVertical, zoomRecortado } from './zoomDeProfundidade';
import type { TrechoDoZoom } from './zoomDeProfundidade';

const ESQUERDA = 52;
const TOPO = 22;
const BAIXO = 24;
const DIREITA = 12;
/** A largura de cada coluna no lado a lado, px. */
const LARGURA_DA_COLUNA = 96;

/** O que a ponte de teste devolve de uma faixa. */
export interface FaixaDaPonte { centro: number; media: number; desvio: number; n: number; texto: string }

export interface PonteDosPerfis {
  faixa(centro: number): FaixaDaPonte | null;
  tracos(): { codigo: string; destacado: boolean }[];
  colunas(): string[];
}

export function PerfisDaCampanha(p: {
  verticais: readonly VerticalDaAnalise[];
  grandeza: Grandeza;
  eixo: 'profundidade' | 'cota';
  modo: 'sobrepostos' | 'lado_a_lado';
  passo: number;
  ordem?: readonly string[];
  destacada?: string | null;
  aoDestacar?(codigo: string | null): void;
  mostrarNaoHomologadas?: boolean;
  altura?: number;
  /**
   * A escala horizontal escolhida, por grandeza (onda 347, R12); sem ela, a de hoje. Com `aoMudarEscala`, o botão
   * «Escala» sobre o gráfico.
   */
  escalas?: Readonly<Record<string, EscalaDoGrafico>>;
  aoMudarEscala?(grandeza: string, e: EscalaDoGrafico | null): void;
  /**
   * O zoom do eixo (onda 347, R13): o trecho, nos valores do eixo (profundidade ou cota), do menor ao maior; null, o
   * eixo inteiro. Com `aoZoom`, o arrasto e a barra «Profundidade (ou Cota) de [ ] a [ ] m».
   */
  zoom?: TrechoDoZoom | null;
  aoZoom?(z: TrechoDoZoom | null): void;
}): JSX.Element {
  const [caixa, largura] = useLargura<HTMLDivElement>();
  const recorte = useId();
  const altura = p.altura ?? 480;
  const [faixaEscolhida, setFaixaEscolhida] = useState<number | null>(null);
  const porCota = p.eixo === 'cota';
  const def = GRANDEZAS[p.grandeza];
  const unidade = def?.unidade ?? '';
  const rotulo = def?.rotulo ?? p.grandeza;

  /* As verticais que entram (por cota, as com cota), na ordem pedida. */
  const entram = useMemo(() => {
    const todas = p.verticais.filter((v) => !porCota || (v.cota !== null && Number.isFinite(v.cota)));
    if (!p.ordem || p.ordem.length === 0) return [...todas].sort((a, b) => a.codigo.localeCompare(b.codigo, 'pt-BR', { numeric: true }));
    const posicao = new Map(p.ordem.map((c, i) => [c, i]));
    return [...todas].sort((a, b) => (posicao.get(a.codigo) ?? 1e9) - (posicao.get(b.codigo) ?? 1e9) || a.codigo.localeCompare(b.codigo, 'pt-BR', { numeric: true }));
  }, [p.verticais, p.ordem, porCota]);
  const semCota = porCota ? p.verticais.filter((v) => v.cota === null || !Number.isFinite(v.cota)).map((v) => v.codigo) : [];

  /* O perfil médio do motor. */
  const perfil = useMemo(() => perfilMedio(
    p.verticais.map((v) => ({ codigo: v.codigo, r: v.r, cota: v.cota })),
    { grandeza: p.grandeza, eixo: p.eixo, passo: p.passo },
  ), [p.verticais, p.grandeza, p.eixo, p.passo]);

  /* A marca de homologação da grandeza (a pior das verticais). */
  const marca = useMemo(() => {
    const marcas = p.verticais.map((v) => marcaDaColuna(p.grandeza, v.r.premissas));
    const naoHomologada = marcas.find((m) => !m.homologada);
    return naoHomologada ?? null;
  }, [p.verticais, p.grandeza]);

  /* As séries de cada vertical no eixo pedido. */
  const series = useMemo(() => entram.map((v) => {
    const z = v.r.col.z;
    const s = v.r.col[p.grandeza] ?? new Float64Array(0);
    const y = porCota ? Float64Array.from(z, (zz) => (v.cota as number) - zz) : Float64Array.from(z);
    return { codigo: v.codigo, y, v: s };
  }), [entram, p.grandeza, porCota]);

  /* Os extremos do eixo vertical (o menor e o maior valor das verticais que entram) e o zoom dentro deles. */
  const extremosY = useMemo((): [number, number] | null => {
    let lo = Infinity, hi = -Infinity;
    for (const s of series) for (let i = 0; i < s.y.length; i++) { if (s.y[i] < lo) lo = s.y[i]; if (s.y[i] > hi) hi = s.y[i]; }
    return lo === Infinity ? null : [lo, hi];
  }, [series]);
  const zoom = useMemo(() => (extremosY ? zoomRecortado(p.zoom, extremosY) : null), [p.zoom, extremosY]);
  const noTrecho = (y: number): boolean => !zoom || (y >= zoom.de - 1e-9 && y <= zoom.ate + 1e-9);

  /* Os domínios. A escala escolhida (onda 347): o tipo e os limites dela; no automático, o de hoje no tipo escolhido
     (no zoom, das leituras do trecho). */
  const ajuste = p.escalas?.[p.grandeza] ?? null;
  const tipoX = ajuste?.tipo ?? 'linear';
  const fixos = limitesFixos(ajuste);
  const dominioX = useMemo((): [number, number] => {
    const valores = series.map((s) => (zoom ? Array.from(s.v).filter((_, i) => s.y[i] >= zoom.de - 1e-9 && s.y[i] <= zoom.ate + 1e-9) : s.v));
    const hoje = dominioDe(valores, tipoX, { comZero: p.grandeza === 'u2' });
    return fixos ? [fixos.min, fixos.max] : ajuste && tipoX === 'log' ? dominioDaEscala(ajuste, hoje) : hoje;
  }, [series, p.grandeza, ajuste, tipoX, fixos, zoom]);
  const marcasX = (alvo: number): number[] => (ajuste ? marcasDaEscala(ajuste, dominioX, alvo) : marcas('linear', dominioX, alvo));
  /** O valor no eixo: com os limites fixos, o de fora vai à borda [esc.corte]; no log, o ≤ 0 fica sem lugar (NaN). */
  const naEscala = (v: number): number => (fixos && Number.isFinite(v) && !(tipoX === 'log' && v <= 0)
    ? Math.min(fixos.max, Math.max(fixos.min, v)) : v);
  const dominioY = useMemo((): [number, number] => {
    if (!extremosY) return porCota ? [1, 0] : [0, 1];
    const [lo, hi] = zoom ? [zoom.de, zoom.ate] : extremosY;
    return porCota ? [hi, lo] : [lo, hi];
  }, [extremosY, zoom, porCota]);

  const alturaDoDesenho = Math.max(1, altura - TOPO - BAIXO);
  const ey = useMemo(() => escala('linear', dominioY, [TOPO, TOPO + alturaDoDesenho]), [dominioY, alturaDoDesenho]);

  /* O arrasto do zoom (com `aoZoom`): para baixo, o trecho; para cima, o eixo inteiro. A prévia: a faixa tracejada. */
  const [previa, setPrevia] = useState<{ a: number; b: number } | null>(null);
  const gesto = useArrastoVertical<SVGSVGElement>({
    caixa,
    valor: (e) => {
      const y = e.clientY - e.currentTarget.getBoundingClientRect().top;
      return ey.de(Math.min(TOPO + alturaDoDesenho, Math.max(TOPO, y)));
    },
    minimo: ARRASTO_DO_ZOOM,
    toqueLongo: true,
    prenderAoApertar: false,
    aoPrevia: (x) => setPrevia(x && x.desce ? { a: x.inicio, b: x.fim } : null),
    aoSoltar: (x) => {
      if (!x.desce) { p.aoZoom?.(null); return; }
      const z = extremosY ? zoomRecortado({ de: x.inicio, ate: x.fim }, extremosY) : null;
      if (z) p.aoZoom?.(z);
    },
  });
  const comGesto = p.aoZoom ? gesto : {};
  const desenhoDaPrevia = (x0: number, x1: number): JSX.Element | null => {
    if (!previa) return null;
    const ya = ey.para(previa.a);
    const yb = ey.para(previa.b);
    return (
      <g className="cptu-perfis-previa">
        <rect className="cptu-selecao cptu-previa-do-zoom" x={x0} width={Math.max(1, x1 - x0)} y={Math.min(ya, yb)} height={Math.max(1, Math.abs(yb - ya))} />
        <text className="cptu-texto cptu-previa-do-zoom-texto" x={x0 + 4} y={Math.min(ya, yb) - 4}>
          {`${porCota ? 'cota ' : ''}${numeroBr(previa.a, 2)} → ${numeroBr(previa.b, 2)} m`}
        </text>
      </g>
    );
  };
  const recorteDoDesenho = (x0: number, x1: number): JSX.Element => (
    <defs>
      <clipPath id={recorte}>
        <rect x={x0} y={TOPO} width={Math.max(1, x1 - x0)} height={alturaDoDesenho} />
      </clipPath>
    </defs>
  );

  /** O índice da faixa do perfil mais perto de uma coordenada do eixo. */
  const faixaMaisPerto = useCallback((coord: number): number | null => {
    if (!perfil.centro.length) return null;
    let melhor = 0;
    for (let i = 1; i < perfil.centro.length; i++) if (Math.abs(perfil.centro[i] - coord) < Math.abs(perfil.centro[melhor] - coord)) melhor = i;
    return melhor;
  }, [perfil]);

  const textoDaFaixa = useCallback((i: number): string => {
    const centro = perfil.centro[i];
    const n = perfil.n[i];
    const media = perfil.media[i];
    const desvio = perfil.desvio[i];
    const u = unidade ? ` ${unidade}` : '';
    const partes = [`${porCota ? 'cota' : 'profundidade'} ${numeroBr(centro, 3)} m`];
    if (n > 0 && Number.isFinite(media)) {
      partes.push(`média ${numeroBr(media, 2)}${u}${n >= 2 && Number.isFinite(desvio) ? ` ± ${numeroBr(desvio, 2)}` : ''}`);
      partes.push(`${n} ${n === 1 ? 'vertical' : 'verticais'}`);
    } else partes.push('sem valor');
    return partes.join(' · ');
  }, [perfil, unidade, porCota]);

  /* A ponte de teste. */
  useEffect(() => {
    const el = caixa.current;
    if (!el) return undefined;
    const ponte: PonteDosPerfis = {
      faixa(centro) {
        const i = faixaMaisPerto(centro);
        if (i === null) return null;
        return { centro: perfil.centro[i], media: perfil.media[i], desvio: perfil.desvio[i], n: perfil.n[i], texto: textoDaFaixa(i) };
      },
      tracos: () => series.map((s) => ({ codigo: s.codigo, destacado: s.codigo === p.destacada })),
      colunas: () => (p.modo === 'lado_a_lado' ? series.map((s) => s.codigo) : []),
    };
    Object.defineProperty(el, 'ponteDosPerfis', { value: ponte, writable: false, enumerable: false, configurable: true });
    return () => { delete (el as unknown as { ponteDosPerfis?: unknown }).ponteDosPerfis; };
  }, [caixa, faixaMaisPerto, perfil, textoDaFaixa, series, p.destacada, p.modo]);

  const aoTeclar = (e: React.KeyboardEvent) => {
    if (!perfil.centro.length) return;
    let i = faixaEscolhida ?? Math.floor(perfil.centro.length / 2);
    if (e.key === 'ArrowUp') i -= 1;
    else if (e.key === 'ArrowDown') i += 1;
    else if (e.key === 'Escape') { setFaixaEscolhida(null); return; }
    else return;
    e.preventDefault();
    setFaixaEscolhida(Math.min(perfil.centro.length - 1, Math.max(0, i)));
  };
  const aoMover = (e: React.MouseEvent<SVGSVGElement>) => {
    const y = e.clientY - e.currentTarget.getBoundingClientRect().top;
    if (y < TOPO || y > TOPO + alturaDoDesenho) return;
    setFaixaEscolhida(faixaMaisPerto(ey.de(y)));
  };

  /** O caminho de uma série (dizimada por pixel), numa escala x. */
  const caminho = (s: { y: Float64Array; v: Float64Array }, ex: { para(v: number): number }): string => {
    const d = dizimarPorPixel(s.y, s.v, { z0: Math.min(dominioY[0], dominioY[1]), z1: Math.max(dominioY[0], dominioY[1]), altura: alturaDoDesenho });
    let saida = '';
    let aberto = false;
    const quebras = new Set(d.quebras);
    for (let i = 0; i < d.z.length; i++) {
      if (quebras.has(i)) aberto = false;
      const x = ex.para(naEscala(d.v[i]));
      const y = ey.para(d.z[i]);
      if (!Number.isFinite(x) || !Number.isFinite(y)) { aberto = false; continue; }
      saida += `${aberto ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`;
      aberto = true;
    }
    return saida;
  };

  const marcasY = marcas('linear', [Math.min(dominioY[0], dominioY[1]), Math.max(dominioY[0], dominioY[1])], 8);
  const eixoY = (ex0: number, ex1: number, comTexto: boolean) => (
    <g>
      {marcasY.map((y) => (
        <g key={`y${y}`}>
          <line className="cptu-grade" x1={ex0} x2={ex1} y1={ey.para(y)} y2={ey.para(y)} />
          {comTexto && <text className="cptu-texto cptu-texto-2" x={ex0 - 6} y={ey.para(y) + 4} textAnchor="end">{numeroBr(y, 2)}</text>}
        </g>
      ))}
      {comTexto && <text className="cptu-texto cptu-texto-2" x={ex0 - 6} y={TOPO - 8} textAnchor="end">{porCota ? 'cota (m)' : 'z (m)'}</text>}
    </g>
  );
  const eixoX = (ex: { para(v: number): number }, x0: number, x1: number, alvo: number) => (
    <g>
      {marcasX(alvo).map((v) => (
        <g key={`x${v}`}>
          <line className="cptu-grade" x1={ex.para(v)} x2={ex.para(v)} y1={TOPO + alturaDoDesenho} y2={TOPO + alturaDoDesenho + 4} />
          <text className="cptu-texto cptu-texto-2" x={ex.para(v)} y={TOPO + alturaDoDesenho + 14} textAnchor="middle">{numeroBr(v, 2)}</text>
        </g>
      ))}
      <text className="cptu-texto cptu-texto-2" x={x1} y={altura - 2} textAnchor="end">{rotulo}{unidade ? ` (${unidade})` : ''}</text>
      <rect className="cptu-moldura" x={x0} y={TOPO} width={Math.max(1, x1 - x0)} height={alturaDoDesenho} fill="none" />
    </g>
  );

  /**
   * O corte de cada série com o máximo fixo: a primeira coordenada do eixo (profundidade ou cota, de cima para baixo)
   * em que ela passa do máximo — o «▸» na borda direita [esc.corte]; null: não passa.
   */
  const cortes = useMemo(() => new Map(series.map((s) => {
    if (!fixos) return [s.codigo, null] as const;
    const dentro = (y: number): boolean => !zoom || (y >= zoom.de - 1e-9 && y <= zoom.ate + 1e-9);
    for (let i = 0; i < s.v.length; i++) if (Number.isFinite(s.v[i]) && s.v[i] > fixos.max && Number.isFinite(s.y[i]) && dentro(s.y[i])) return [s.codigo, s.y[i]] as const;
    return [s.codigo, null] as const;
  })), [series, fixos, zoom]);
  const marcaDoCorte = (codigo: string, x: number): JSX.Element | null => {
    const y = cortes.get(codigo);
    if (y === null || y === undefined || !fixos) return null;
    return (
      <text key={`corte-${codigo}`} className="cptu-texto cptu-corte" data-codigo={codigo} data-z={y} x={x} y={ey.para(y) + 4} textAnchor="end">
        <title>{`${codigo}: ${rotulo} passa de ${numeroBr(fixos.max, 2)}${unidade ? ` ${unidade}` : ''} a ${porCota ? 'cota' : 'profundidade'} ${numeroBr(y, 2)} m — cortado na borda`}</title>
        ▸
      </text>
    );
  };

  const faixaDoCursor = faixaEscolhida !== null && faixaEscolhida < perfil.centro.length && noTrecho(perfil.centro[faixaEscolhida])
    ? faixaEscolhida : null;
  const cursorY = faixaDoCursor !== null ? ey.para(perfil.centro[faixaDoCursor]) : null;

  const sobrepostos = () => {
    const ex = escala(tipoX, dominioX, [ESQUERDA, Math.max(ESQUERDA + 1, largura - DIREITA)]);
    // A faixa ± desvio: um polígono pelas faixas com n ≥ 2.
    let banda = '';
    const esquerda: string[] = [];
    const direita: string[] = [];
    for (let i = 0; i < perfil.centro.length; i++) {
      if (!(perfil.n[i] >= 2) || !Number.isFinite(perfil.desvio[i])) continue;
      const y = ey.para(perfil.centro[i]).toFixed(1);
      const xa = ex.para(naEscala(perfil.media[i] - perfil.desvio[i]));
      const xb = ex.para(naEscala(perfil.media[i] + perfil.desvio[i]));
      if (!Number.isFinite(xa) || !Number.isFinite(xb)) continue; // no log, a ponta ≤ 0 não tem lugar
      esquerda.push(`${xa.toFixed(1)},${y}`);
      direita.unshift(`${xb.toFixed(1)},${y}`);
    }
    if (esquerda.length > 1) banda = `M${[...esquerda, ...direita].join('L')}Z`;
    let media = '';
    let aberto = false;
    for (let i = 0; i < perfil.centro.length; i++) {
      const xm = ex.para(naEscala(perfil.media[i]));
      if (!(perfil.n[i] >= 1) || !Number.isFinite(perfil.media[i]) || !Number.isFinite(xm)) { aberto = false; continue; }
      media += `${aberto ? 'L' : 'M'}${xm.toFixed(1)},${ey.para(perfil.centro[i]).toFixed(1)}`;
      aberto = true;
    }
    return (
      <svg className="cptu-perfis-svg" width={largura} height={altura} role="img"
        aria-label={`Perfis de ${rotulo} sobrepostos: ${series.length} ${series.length === 1 ? 'vertical' : 'verticais'} e a média`}
        data-de={Math.min(dominioY[0], dominioY[1])} data-ate={Math.max(dominioY[0], dominioY[1])}
        onMouseMove={aoMover} onMouseLeave={() => setFaixaEscolhida(null)} {...comGesto}>
        {recorteDoDesenho(ESQUERDA, largura - DIREITA)}
        {eixoY(ESQUERDA, largura - DIREITA, true)}
        {eixoX(ex, ESQUERDA, largura - DIREITA, 5)}
        <g clipPath={`url(#${recorte})`}>
          {banda && <path className="cptu-perfis-banda" d={banda} />}
          {series.map((s) => (
            <path key={s.codigo} className={`cptu-perfis-traco${s.codigo === p.destacada ? ' destacado' : ''}`} d={caminho(s, ex)}
              data-codigo={s.codigo} onClick={() => p.aoDestacar?.(s.codigo === p.destacada ? null : s.codigo)}>
              <title>{s.codigo}</title>
            </path>
          ))}
          {media && <path className="cptu-perfis-media" d={media} />}
        </g>
        {series.map((s) => marcaDoCorte(s.codigo, ex.para(fixos?.max ?? NaN) - 1))}
        {cursorY !== null && <line className="cptu-perfis-cursor" x1={ESQUERDA} x2={largura - DIREITA} y1={cursorY} y2={cursorY} />}
        {desenhoDaPrevia(ESQUERDA, largura - DIREITA)}
      </svg>
    );
  };

  const ladoALado = () => {
    const larguraDaColuna = Math.max(60, Math.min(LARGURA_DA_COLUNA, Math.floor((largura - ESQUERDA - DIREITA) / Math.max(1, series.length))));
    const larguraTotal = ESQUERDA + larguraDaColuna * series.length + DIREITA;
    return (
      <div className="cptu-perfis-colunas rola">
        <svg className="cptu-perfis-svg" width={larguraTotal} height={altura} role="img"
          aria-label={`Perfis de ${rotulo} lado a lado: ${series.map((s) => s.codigo).join(', ')}`}
          data-de={Math.min(dominioY[0], dominioY[1])} data-ate={Math.max(dominioY[0], dominioY[1])}
          onMouseMove={aoMover} onMouseLeave={() => setFaixaEscolhida(null)} {...comGesto}>
          {recorteDoDesenho(ESQUERDA, ESQUERDA + larguraDaColuna * series.length)}
          {eixoY(ESQUERDA, ESQUERDA + larguraDaColuna * series.length, true)}
          {series.map((s, k) => {
            const x0 = ESQUERDA + k * larguraDaColuna;
            const ex = escala(tipoX, dominioX, [x0 + 4, x0 + larguraDaColuna - 4]);
            return (
              <g key={s.codigo} className={`cptu-perfis-coluna${s.codigo === p.destacada ? ' destacado' : ''}`} data-codigo={s.codigo}>
                <rect className="cptu-moldura" x={x0} y={TOPO} width={larguraDaColuna} height={alturaDoDesenho} fill="none" />
                <text className="cptu-texto cptu-secao-codigo" x={x0 + larguraDaColuna / 2} y={TOPO - 8} textAnchor="middle">{s.codigo}</text>
                <path className={`cptu-perfis-traco${s.codigo === p.destacada ? ' destacado' : ''}`} d={caminho(s, ex)}
                  clipPath={`url(#${recorte})`} onClick={() => p.aoDestacar?.(s.codigo === p.destacada ? null : s.codigo)} />
                {marcaDoCorte(s.codigo, ex.para(fixos?.max ?? NaN) - 1)}
                {marcasX(2).map((v) => (
                  <text key={`x${v}`} className="cptu-texto cptu-texto-2" x={ex.para(v)} y={TOPO + alturaDoDesenho + 14} textAnchor="middle">{numeroBr(v, 1)}</text>
                ))}
              </g>
            );
          })}
          <text className="cptu-texto cptu-texto-2" x={larguraTotal - DIREITA} y={altura - 2} textAnchor="end">{rotulo}{unidade ? ` (${unidade})` : ''}</text>
          {cursorY !== null && <line className="cptu-perfis-cursor" x1={ESQUERDA} x2={ESQUERDA + larguraDaColuna * series.length} y1={cursorY} y2={cursorY} />}
          {desenhoDaPrevia(ESQUERDA, ESQUERDA + larguraDaColuna * series.length)}
        </svg>
      </div>
    );
  };

  return (
    <div ref={caixa} className="cptu-perfis" role="group" tabIndex={0} onKeyDown={aoTeclar}
      aria-label={`Perfis de ${rotulo} por ${porCota ? 'cota' : 'profundidade'}: setas sobem e descem uma faixa, Esc limpa`}>
      {p.aoMudarEscala ? (
        <div className="cptu-perfis-escala">
          <ControleDaEscala grandeza={p.grandeza} rotulo={rotulo} unidade={unidade} escala={ajuste} dominio={dominioX}
            aoMudar={(e) => p.aoMudarEscala?.(p.grandeza, e)} />
        </div>
      ) : null}
      {p.aoZoom && extremosY ? (
        <BarraDoZoom zoom={zoom} limites={extremosY} rotulo={porCota ? 'Cota' : 'Profundidade'} aoZoom={(z) => p.aoZoom?.(z)} />
      ) : null}
      {marca && !marca.homologada && (p.mostrarNaoHomologadas ?? true) ? (
        <p className="cptu-perfis-marca"><b>▲ não homologada</b>{marca.motivo ? ` — ${marca.motivo}` : ''}</p>
      ) : null}
      {largura > 0 && series.length > 0 ? (p.modo === 'sobrepostos' ? sobrepostos() : ladoALado()) : (
        <p className="cptu-nota">{series.length === 0 ? 'Nenhuma vertical para desenhar.' : ''}</p>
      )}
      <p className="cptu-perfis-leitura" aria-live="polite">{faixaDoCursor !== null ? textoDaFaixa(faixaDoCursor) : ''}</p>
      <div className="cptu-perfis-legenda">
        <ul className="cptu-perfis-lista">
          {series.map((s) => (
            <li key={s.codigo}>
              <button type="button" className={`cptu-perfis-item${s.codigo === p.destacada ? ' destacado' : ''}`}
                aria-pressed={s.codigo === p.destacada} onClick={() => p.aoDestacar?.(s.codigo === p.destacada ? null : s.codigo)}>
                <span className="cptu-perfis-amostra" aria-hidden="true" /> {s.codigo}
              </button>
            </li>
          ))}
        </ul>
        <p className="cptu-nota">
          {p.modo === 'sobrepostos' ? 'Traço fino: cada vertical; traço grosso: a média das verticais na faixa, com a faixa ± desvio (só onde há 2 ou mais). ' : 'Uma coluna por vertical, no mesmo eixo. '}
          Faixas de {numeroBr(p.passo, 2)} m.
          {semCota.length > 0 ? ` Sem cota: ${semCota.join(', ')}.` : ''}
        </p>
      </div>
    </div>
  );
}
